import './shell.css';
import type { GameState } from '../api/types';
import type { Notice, Tab, TabId, TabShowOptions } from '../tabs/contract';
import type { Shell } from './shell-contract';
import { deckle } from '../core/deckle';
import { remainingMs } from '../core/clock';
import { shouldAnimate, touch } from '../core/tabtimer';
import { injectFonts } from './fonts';
import { NoticeQueue, TAB_INTRO, fmtBandRemaining, fmtScore, nextRankGap, rankOf, rankingRows } from './shell-logic';
import { cardsLocked, promptsLockRemaining } from '../core/unlock';

const TAB_ORDER: TabId[] = ['map', 'book', 'prompts', 'cards'];
const TAB_CLASS: Record<TabId, string> = { map: 'sh-t-map', book: 'sh-t-book', prompts: 'sh-t-prompts', cards: 'sh-t-cards' };
const INTRO_KEY = 'waggle.intro.v1';
const SHOW_MS = 4000;
const OUT_MS = 300;
const NS = 'http://www.w3.org/2000/svg';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(INTRO_KEY);
    const a = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function saveSeen(s: Set<string>): void {
  try {
    localStorage.setItem(INTRO_KEY, JSON.stringify([...s]));
  } catch {
    /* 保存できなければ次回また出る */
  }
}

/** 紐の輪（試作 notice_band の SVG をそのまま） */
function twine(cls: string, d: string): SVGSVGElement {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', `sh-tw ${cls}`);
  svg.setAttribute('viewBox', '0 0 50 24');
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS(NS, 'path');
  p.setAttribute('d', d);
  p.setAttribute('fill', 'none');
  p.setAttribute('stroke', '#1E1712');
  p.setAttribute('stroke-width', '1');
  p.setAttribute('stroke-linecap', 'round');
  svg.appendChild(p);
  return svg;
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 要素の下端が root の上端から何pxか（transform を含まない layout の位置。出入りの回転で測るとずれる） */
function layoutBottom(target: HTMLElement, root: HTMLElement): number | null {
  let y = target.offsetHeight;
  let n: HTMLElement | null = target;
  while (n && n !== root) {
    y += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  return n === root ? y : null;
}

export function createShell(root: HTMLElement): Shell {
  injectFonts();

  const stage = el('div', 'sh-stage');
  const band = el('div', 'sh-band');
  const rankBox = el('div', 'sh-rank');
  const side = el('div', 'sh-side');
  const ptsRow = el('div');
  const ptsNum = el('b', 'sh-num', '0');
  ptsRow.append(ptsNum, document.createTextNode(' 点'));
  const timeRow = el('div');
  const timeNum = el('b', 'sh-num', '--:--');
  timeRow.append(document.createTextNode('残り '), timeNum);
  side.append(ptsRow, timeRow);
  band.append(rankBox, side);
  band.setAttribute('role', 'button');
  band.setAttribute('aria-label', '全チームの点を見る');
  band.setAttribute('aria-expanded', 'false');

  // 帯を押すと出る全チームの点
  const board = el('div', 'sh-board');
  board.hidden = true;
  const boardPaper = el('div', 'sh-board-paper');
  board.appendChild(boardPaper);

  const content = el('div', 'sh-content');
  const conn = el('div', 'sh-conn');
  conn.hidden = true;
  conn.setAttribute('role', 'status');
  const nav = el('nav', 'sh-nav');
  nav.setAttribute('aria-label', '画面の切り替え');
  stage.append(content, board, band, conn, nav);
  root.appendChild(stage);

  const tabs = new Map<TabId, Tab>();
  const buttons = new Map<TabId, HTMLButtonElement>();
  let current: TabId | null = null;
  let state: GameState | null = null;
  const seenIntro = loadSeen();

  // ---- 上部の帯
  function renderBand(): void {
    const rank = state ? rankOf(state.ranking, state.team.team_id) : null;
    rankBox.replaceChildren();
    if (rank !== null) rankBox.append(el('b', 'sh-num', String(rank)), el('span', undefined, '位'));
    const next = state ? nextRankGap(state.ranking, state.team.team_id) : null;
    if (next) {
      const g = el('span', 'sh-gap-next');
      g.append(`${next.rank}位まで あと`, el('b', 'sh-num', fmtScore(next.gap)), '点');
      rankBox.append(g);
    }
    ptsNum.textContent = state ? fmtScore(state.team.total) : '0';
    tickTime();
    if (!board.hidden) renderBoard();
  }

  // ---- 全チームの点（順位を見せない開催では開かない）
  function renderBoard(): void {
    if (!state?.ranking) {
      closeBoard();
      return;
    }
    const list = el('ol', 'sh-board-list');
    for (const r of rankingRows(state.ranking)) {
      const li = el('li');
      if (r.team_id === state.team.team_id) li.classList.add('sh-me');
      li.append(
        el('b', 'sh-num sh-board-rank', String(r.rank)),
        el('span', 'sh-board-name', r.team_name),
        el('b', 'sh-num sh-board-pts', fmtScore(r.total)),
        el('span', 'sh-board-unit', '点'),
      );
      list.appendChild(li);
    }
    boardPaper.replaceChildren(list);
  }
  function openBoard(): void {
    if (!state?.ranking) return;
    // 荷札が表に重なるので先に下げる
    if (currentEl) dismiss(currentEl, true);
    renderBoard();
    board.hidden = false;
    band.setAttribute('aria-expanded', 'true');
  }
  function closeBoard(): void {
    board.hidden = true;
    band.setAttribute('aria-expanded', 'false');
  }
  band.addEventListener('click', () => (board.hidden ? openBoard() : closeBoard()));
  board.addEventListener('click', closeBoard);
  function tickTime(): void {
    const ends = state?.event.ends_at ?? null;
    timeNum.textContent = fmtBandRemaining(ends ? remainingMs(ends) : null);
  }
  window.setInterval(tickTime, 1000);

  // ---- お題の解禁前（024）
  const lockLabel = el('span', 'sh-lock');
  function renderLock(): void {
    const b = buttons.get('prompts');
    if (!b) return;
    const n = promptsLockRemaining(state);
    b.classList.toggle('sh-locked', n > 0);
    if (n > 0) {
      lockLabel.textContent = `あと${n}個で解禁`;
      if (!lockLabel.isConnected) b.appendChild(lockLabel);
    } else {
      lockLabel.remove();
    }
    renderCardsLock();
  }

  // ---- カードを初めて手に入れるまでカードのタブを開けない
  const cardsLockLabel = el('span', 'sh-lock');
  function cardsLockText(): { label: string; body: string } {
    const next = state?.event.next_card_at ?? null;
    const at = next ? fmtClock(next) : '';
    return at
      ? { label: `${at}に配布`, body: `最初のカードは${at}に配られます` }
      : { label: 'まだありません', body: 'カードを手に入れると開けます' };
  }
  function renderCardsLock(): void {
    const b = buttons.get('cards');
    if (!b) return;
    const locked = cardsLocked(state);
    b.classList.toggle('sh-locked', locked);
    if (locked) {
      cardsLockLabel.textContent = cardsLockText().label;
      if (!cardsLockLabel.isConnected) b.appendChild(cardsLockLabel);
      if (current === 'cards') select('map');
    } else {
      cardsLockLabel.remove();
    }
  }

  // ---- 荷札
  const queue = new NoticeQueue();
  let currentEl: HTMLElement | null = null;
  let currentTimer: number | undefined;

  function buildTag(n: Notice, tallHeight: number | null): HTMLElement {
    const tag = el('div', 'sh-tag');
    tag.setAttribute('role', 'status');
    if (n.small) tag.classList.add('sh-small');
    tag.append(
      twine('sh-bk', 'M19 9.6C12 10 7.2 12 7.8 15.4 8.4 18.2 14 19.2 23 18.6'),
      twine('sh-fr', 'M41 10C33 9.4 26 9.1 19 9.6'),
    );
    const w = el('div', 'sh-w');
    const f = el('div', 'sh-f');
    const row = el('div', 'sh-row');
    row.append(el('span', 'sh-k', n.heading), el('span', 'sh-tm', n.at ? fmtClock(n.at) : ''));
    f.append(el('span', 'sh-eye'), row, el('span', 'sh-m', n.body));
    if (tallHeight !== null) {
      tag.classList.add('sh-tall');
      f.style.height = `${tallHeight}px`;
    }
    w.appendChild(f);
    tag.appendChild(w);
    return tag;
  }

  /** 地図タブでは data-notice-floor の下端まで隠す高さ。無ければ null（既定の高さ） */
  function tallHeightFor(n: Notice): number | null {
    if (n.sticky || !current) return null;
    const tab = tabs.get(current);
    const floor = tab?.el.querySelector<HTMLElement>('[data-notice-floor]');
    if (!floor || floor.offsetParent === null) return null;
    const bottom = layoutBottom(floor, stage);
    if (bottom === null) return null;
    const top = band.offsetHeight + 10;
    const h = bottom - top;
    return h > 40 ? h : null;
  }

  function showNext(): void {
    const n = queue.take();
    if (!n) return;
    const tag = buildTag(n, tallHeightFor(n));
    currentEl = tag;
    if (n.sticky) tag.dataset.sticky = '1';
    stage.appendChild(tag);
    tag.addEventListener('click', () => dismiss(tag, true));
    // タブの説明も含め、荷札は4秒で消える（本人11/3）
    currentTimer = window.setTimeout(() => dismiss(tag, false), SHOW_MS);
  }

  /** immediate＝タップ。次を待たせず出す。そうでなければ抜けてから次を出す */
  function dismiss(tag: HTMLElement, immediate: boolean): void {
    if (tag.classList.contains('sh-out')) return;
    tag.classList.add('sh-out');
    const isCurrent = tag === currentEl;
    if (isCurrent) {
      window.clearTimeout(currentTimer);
      currentEl = null;
    }
    const finish = (): void => {
      tag.remove();
      if (isCurrent && !immediate) {
        queue.release();
        showNext();
      }
    };
    if (isCurrent && immediate) {
      queue.release();
      showNext();
    }
    window.setTimeout(finish, OUT_MS);
  }

  function notify(n: Notice): void {
    const eff = queue.push(n);
    if (eff === 'show') showNext();
    else if (eff === 'dismiss-current' && currentEl) dismiss(currentEl, false);
  }

  // ---- 通信の状態
  function setConnection(msg: string | null): void {
    if (!msg) {
      conn.hidden = true;
      return;
    }
    conn.textContent = msg;
    conn.hidden = false;
    conn.style.clipPath = deckle(conn.offsetWidth, conn.offsetHeight, 313);
  }

  // ---- タブ
  function maybeIntro(id: TabId): void {
    // 前のタブの説明が残っていれば下げる（説明はそのタブを見ているあいだだけ）
    if (currentEl?.dataset.sticky === '1') dismiss(currentEl, true);
    if (seenIntro.has(id)) return;
    seenIntro.add(id);
    saveSeen(seenIntro);
    const i = TAB_INTRO[id];
    notify({ heading: i.name, body: i.text, sticky: true, small: true });
  }

  function select(id: TabId, focus?: TabShowOptions['focus']): void {
    const next = tabs.get(id);
    if (!next) return;
    closeBoard();
    if (id === 'cards' && cardsLocked(state)) {
      // 何度押しても同じ荷札は重ねない
      const body = cardsLockText().body;
      if (queue.current?.body !== body && !queue.pending.some((p) => p.body === body)) {
        notify({ heading: TAB_INTRO.cards.name, body, small: true });
      }
      return;
    }
    if (current === id && !focus) return;
    if (current && current !== id) {
      const prev = tabs.get(current);
      prev?.hide();
      if (prev) prev.el.style.display = 'none';
      touch(current);
    }
    const changed = current !== id;
    current = id;
    for (const [tid, b] of buttons) {
      if (tid === id) b.setAttribute('aria-current', 'page');
      else b.removeAttribute('aria-current');
    }
    next.el.style.display = '';
    const animate = changed ? shouldAnimate(id) : false;
    try {
      const r = next.show({ animate, focus });
      if (r instanceof Promise) r.catch((e: unknown) => console.error('tab show failed', id, e));
    } catch (e) {
      console.error('tab show failed', id, e);
    }
    if (changed) maybeIntro(id);
  }

  function mount(list: Tab[]): void {
    for (const t of list) {
      tabs.set(t.id, t);
      t.el.style.display = 'none';
      content.appendChild(t.el);
    }
    for (const id of TAB_ORDER) {
      if (!tabs.has(id)) continue;
      const b = el('button', `sh-tab ${TAB_CLASS[id]}`);
      b.type = 'button';
      const name = TAB_INTRO[id].name;
      b.appendChild(el('span', 'sh-body'));
      if (id === 'map') b.appendChild(el('span', 'sh-gap', name));
      else if (id === 'book') b.appendChild(el('span', 'sh-label', name));
      else b.appendChild(document.createTextNode(name));
      b.addEventListener('click', () => select(id));
      buttons.set(id, b);
      nav.appendChild(b);
    }
    select('map');
  }

  return {
    mount,
    select,
    notify,
    update(s: GameState): void {
      state = s;
      renderBand();
      renderLock();
    },
    setConnection,
  };
}
